import { Box, Chip, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, type RefObject } from 'react';

import { Button, Icon } from '@verantos-dev/shared-components';
import type {
  Message as ChatWindowMessage,
  ProgressState as ChatWindowProgressState,
} from '@verantos-dev/shared-components';

import { getConfidenceColor } from '../constants/event-colors';
import type { GatewayMessage, GatewayProgressState } from '../types/gateway-chat-types';

export const GatewayMessageContent = ({ message }: { message: GatewayMessage }) => {
  const content = message.content || (message.status === 'streaming' ? '...' : '');
  const metadata = message.role === 'assistant' ? message.metadata : undefined;

  const handleOpenMappingDialog = () => {
    const sessionId = metadata?.omop_session_info?.session_id;
    const fileName = metadata?.omop_session_info?.file_name;

    if (sessionId && fileName) {
      // Store session data in localStorage for the dialog to access
      localStorage.setItem('omop_session_id', sessionId);
      localStorage.setItem('omop_file_name', fileName);
      if (metadata?.omop_session_info?.original_file_name) {
        localStorage.setItem(
          'omop_original_file_name',
          metadata.omop_session_info.original_file_name
        );
      }
      if (metadata?.omop_session_info?.total_codes) {
        localStorage.setItem('omop_total_codes', metadata.omop_session_info.total_codes.toString());
      }

      // Dispatch the custom event to trigger the dialog
      window.dispatchEvent(
        new CustomEvent('omop-session-updated', {
          detail: {
            session_id: sessionId,
            file_name: fileName,
            original_file_name: metadata?.omop_session_info?.original_file_name,
            total_codes: metadata?.omop_session_info?.total_codes,
            s3_uri: metadata?.omop_session_info?.s3_uri,
            status: metadata?.omop_session_info?.status,
            from_button_click: true, // Flag to indicate this came from button click
          },
        })
      );
    }
  };

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      {content ? (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {content}
        </Typography>
      ) : null}

      {message.status && message.status !== 'complete' ? (
        <Typography variant="caption" color="text.secondary">
          Status: {message.status}
        </Typography>
      ) : null}

      {metadata ? (
        <Stack spacing={1} sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
          {metadata.tools_used?.length ? (
            <Stack spacing={0.5}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Tools Used
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {metadata.tools_used.map((tool) => (
                  <Chip
                    key={tool}
                    size="small"
                    label={tool}
                    sx={{ borderRadius: '8px', fontSize: '0.75rem' }}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Stack>
          ) : null}

          {metadata.citations?.length ? (
            <Stack spacing={0.5}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Sources
              </Typography>
              <Stack spacing={0.5}>
                {metadata.citations.map((citation, index) => (
                  <Typography
                    key={`${citation.source}-${index}`}
                    variant="caption"
                    color="text.secondary"
                  >
                    {citation.source}
                    {citation.count ? ` (n=${citation.count})` : ''}
                    {citation.records ? ` (${citation.records} records)` : ''}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          ) : null}

          {typeof metadata.confidence === 'number' ? (
            <Stack spacing={0.5}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Confidence
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ flex: 1, height: 6, borderRadius: 999, backgroundColor: 'divider' }}>
                  <Box
                    sx={{
                      width: `${Math.round(metadata.confidence * 100)}%`,
                      height: '100%',
                      borderRadius: 999,
                      backgroundColor: getConfidenceColor(metadata.confidence),
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {Math.round(metadata.confidence * 100)}%
                </Typography>
              </Stack>
            </Stack>
          ) : null}

          {metadata.results ? (
            <Stack spacing={0.5}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Results
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Object.keys(metadata.results).length} value
                {Object.keys(metadata.results).length === 1 ? '' : 's'}
              </Typography>
            </Stack>
          ) : null}

          {/* Mapping Results Button */}
          {metadata.omop_session_info ? (
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Button
                id="review-mapping-results"
                displayLabel="Review mapping results"
                variant="contained"
                size="small"
                startIcon={<Icon id="bar-chart-icon" icon="viewResults" width={20} height={20} />}
                onClickHandler={() => handleOpenMappingDialog()}
                sx={{
                  alignSelf: 'flex-start',
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              />
            </Stack>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
};

const adaptMessages = (messages: GatewayMessage[]): ChatWindowMessage[] =>
  messages.map((message) => ({
    currentUser: message.role === 'user' ? 'user' : 'research_assistant',
    isCurrentUser: message.role === 'user',
    timestamp: message.timestamp,
    message: <GatewayMessageContent message={message} />,
  }));

const toChatWindowProgressState = (
  progressState: GatewayProgressState
): ChatWindowProgressState | undefined => {
  if (!progressState.active) {
    return undefined;
  }

  let message = progressState.message;

  if (progressState.percentage && progressState.percentage < 100) {
    message = `${progressState.message} ${progressState.percentage}%`;
  }

  return {
    currentStep: { step: message },
  };
};

export interface UseGatewayChatPresentationOptions {
  messages: GatewayMessage[];
  progressState: GatewayProgressState;
  messagesEndRef: RefObject<HTMLDivElement>;
}

export const useGatewayChatPresentation = ({
  messages,
  progressState,
  messagesEndRef,
}: UseGatewayChatPresentationOptions) => {
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, messagesEndRef]);

  const chatMessages = useMemo(() => adaptMessages(messages), [messages]);
  const chatProgressState = useMemo(
    () => toChatWindowProgressState(progressState),
    [progressState]
  );

  return {
    chatMessages,
    chatProgressState,
  };
};
